import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminSidebar from '../../../components/admin/AdminSidebar';
import AdminHeader from '../../../components/admin/AdminHeader';
import BookingDetails from '../../../components/admin/BookingDetails';
import { buildApiUrl } from '../../../config/api';

const AdminCampingBookings = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [, setStats] = useState({
        total: 0,
        confirmed: 0,
        pending: 0,
        cancelled: 0,
        completed: 0,
        totalRevenue: 0
    });
    const [filters, setFilters] = useState({
        status: '',
        payment_status: '',
        date_from: '',
        date_to: '',
        search: '',
        location_id: '',
        site_id: ''
    });
    const [locations, setLocations] = useState([]);
    const [sites, setSites] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 1
    });

    // Get token from localStorage
    const getToken = () => {
        const token = localStorage.getItem('token');
        return token;
    };

    // Fetch locations
    const fetchLocations = useCallback(async () => {
        try {
            const token = getToken();
            const response = await axios.get(
                buildApiUrl('/camping/locations'),
                {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                }
            );
            
            if (response.data.success) {
                setLocations(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching locations:', error);
        }
    }, []);

    // Fetch sites by location
    const fetchSitesByLocation = useCallback(async (locationId) => {
        try {
            const token = getToken();
            const response = await axios.get(
                buildApiUrl(`/camping/sites/location/${locationId}`),
                {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                }
            );
            
            if (response.data.success) {
                setSites(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching sites:', error);
        }
    }, []);

    // Fetch bookings with filters
    const fetchBookings = useCallback(async () => {
        try {
            setLoading(true);
            const token = getToken();
            
            // Build query params
            const params = new URLSearchParams();
            
            if (filters.search) params.append('search', filters.search);
            if (filters.status) params.append('status', filters.status);
            if (filters.payment_status) params.append('payment_status', filters.payment_status);
            if (filters.date_from) params.append('date_from', filters.date_from);
            if (filters.date_to) params.append('date_to', filters.date_to);
            if (filters.location_id) params.append('location_id', filters.location_id);
            if (filters.site_id) params.append('site_id', filters.site_id);
            
            params.append('page', pagination.page);
            params.append('limit', pagination.limit);
            
            console.log('Fetching bookings with params:', params.toString());
            
            // ✅ Correct endpoint with auth
            const response = await axios.get(
                `${buildApiUrl('/camping/admin/bookings')}?${params.toString()}`,
                {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                }
            );
            
            console.log('Bookings response:', response.data);
            
            if (response.data.success) {
                const responseData = response.data.data;
                
                // ✅ Handle response structure: { success: true, data: { bookings, total, page, pages, limit } }
                if (responseData && Array.isArray(responseData.bookings)) {
                    setBookings(responseData.bookings);
                    setPagination({
                        page: responseData.page || 1,
                        limit: responseData.limit || 10,
                        total: responseData.total || 0,
                        pages: responseData.pages || 1
                    });
                } else if (Array.isArray(responseData)) {
                    setBookings(responseData);
                } else {
                    setBookings([]);
                }
            } else {
                setBookings([]);
            }
        } catch (error) {
            console.error('Error fetching bookings:', error);
            if (error.response?.status === 401) {
                alert('Session expired. Please login again.');
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/admin/login';
            }
            setBookings([]);
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.limit, pagination.page]);

    // Fetch stats
    const fetchStats = useCallback(async () => {
        try {
            const token = getToken();
            const params = new URLSearchParams();
            if (filters.date_from) params.append('date_from', filters.date_from);
            if (filters.date_to) params.append('date_to', filters.date_to);
            if (filters.location_id) params.append('location_id', filters.location_id);
            
            const response = await axios.get(
                `${buildApiUrl('/camping/admin/bookings/stats')}?${params.toString()}`,
                {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                }
            );
            
            if (response.data.success) {
                const statsData = response.data.data;
                setStats({
                    total: statsData.total_bookings || 0,
                    confirmed: statsData.confirmed_bookings || 0,
                    pending: statsData.pending_bookings || 0,
                    cancelled: statsData.cancelled_bookings || 0,
                    completed: statsData.completed_bookings || 0,
                    totalRevenue: statsData.total_revenue || 0
                });
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    }, [filters.date_from, filters.date_to, filters.location_id]);

    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    useEffect(() => {
        if (filters.location_id) {
            fetchSitesByLocation(filters.location_id);
        } else {
            setSites([]);
        }
    }, [fetchSitesByLocation, filters.location_id]);

    useEffect(() => {
        fetchBookings();
        fetchStats();
    }, [fetchBookings, fetchStats]);

    // Update booking status
    const handleStatusUpdate = async (id, status) => {
        if (!window.confirm(`Are you sure you want to change status to ${status}?`)) {
            return;
        }
        
        try {
            const token = getToken();
            if (!token) {
                alert('Please login to perform this action');
                window.location.href = '/admin/login';
                return;
            }
            
            await axios.patch(
                buildApiUrl(`/camping/admin/bookings/${id}/status`),
                { status },
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            alert('✅ Booking status updated successfully');
            fetchBookings();
            fetchStats();
        } catch (error) {
            console.error('Error updating booking:', error);
            if (error.response?.status === 401) {
                alert('Session expired. Please login again.');
                localStorage.removeItem('token');
                window.location.href = '/admin/login';
            } else {
                alert(error.response?.data?.message || 'Failed to update booking status');
            }
        }
    };

    // Update payment status
    const handlePaymentUpdate = async (id, payment_status) => {
        if (!window.confirm(`Are you sure you want to change payment to ${payment_status}?`)) {
            return;
        }
        
        try {
            const token = getToken();
            if (!token) {
                alert('Please login to perform this action');
                window.location.href = '/admin/login';
                return;
            }
            
            await axios.patch(
                buildApiUrl(`/camping/admin/bookings/${id}/payment`),
                { payment_status },
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            alert('✅ Payment status updated successfully');
            fetchBookings();
            fetchStats();
        } catch (error) {
            console.error('Error updating payment:', error);
            if (error.response?.status === 401) {
                alert('Session expired. Please login again.');
                localStorage.removeItem('token');
                window.location.href = '/admin/login';
            } else {
                alert(error.response?.data?.message || 'Failed to update payment status');
            }
        }
    };

    // Export bookings
    const handleExport = async () => {
        try {
            const token = getToken();
            if (!token) {
                alert('Please login to export data');
                window.location.href = '/admin/login';
                return;
            }
            
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.payment_status) params.append('payment_status', filters.payment_status);
            if (filters.date_from) params.append('date_from', filters.date_from);
            if (filters.date_to) params.append('date_to', filters.date_to);
            if (filters.location_id) params.append('location_id', filters.location_id);
            if (filters.site_id) params.append('site_id', filters.site_id);
            
            const response = await axios.get(
                `${buildApiUrl('/camping/admin/bookings/export')}?${params.toString()}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` },
                    responseType: 'blob'
                }
            );
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const date = new Date().toISOString().split('T')[0];
            link.setAttribute('download', `camping-bookings-${date}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            alert('✅ Export completed successfully');
        } catch (error) {
            console.error('Error exporting bookings:', error);
            if (error.response?.status === 401) {
                alert('Session expired. Please login again.');
                localStorage.removeItem('token');
                window.location.href = '/admin/login';
            } else {
                alert('Failed to export bookings');
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return 'Invalid Date';
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'Invalid Date';
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '₹0';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const clearFilters = () => {
        setFilters({
            status: '',
            payment_status: '',
            date_from: '',
            date_to: '',
            search: '',
            location_id: '',
            site_id: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const removeFilter = (filterName) => {
        if (filterName === 'location_id') {
            setFilters(prev => ({ ...prev, location_id: '', site_id: '' }));
        } else {
            setFilters(prev => ({ ...prev, [filterName]: '' }));
        }
    };

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main">
                <AdminHeader title="Camping Bookings" />
                
                <div className="admin-camping-bookings">
                    {/* Stats Cards */}
              
                    {/* Filters Section */}
                    <div className="filters-section">
                        <div className="filters-header">
                            <h3>Filter Bookings</h3>
                            <button 
                                className="btn-export" 
                                onClick={handleExport} 
                                disabled={loading || bookings.length === 0}
                            >
                                <i className="fas fa-download"></i>
                                Export CSV
                            </button>
                        </div>

                        <div className="filters-grid">
                            <div className="filter-group">
                                <label>Search</label>
                                <input
                                    type="text"
                                    placeholder="Search by name, email, booking ID..."
                                    value={filters.search}
                                    onChange={(e) => setFilters({...filters, search: e.target.value, page: 1})}
                                    className="filter-input"
                                />
                            </div>

                            <div className="filter-group">
                                <label>Location</label>
                                <select 
                                    value={filters.location_id} 
                                    onChange={(e) => setFilters({...filters, location_id: e.target.value, site_id: '', page: 1})}
                                    className="filter-select"
                                >
                                    <option value="">All Locations</option>
                                    {locations.map(loc => (
                                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-group">
                                <label>Camping Site</label>
                                <select 
                                    value={filters.site_id} 
                                    onChange={(e) => setFilters({...filters, site_id: e.target.value, page: 1})}
                                    className="filter-select"
                                    disabled={!filters.location_id}
                                >
                                    <option value="">All Sites</option>
                                    {sites.map(site => (
                                        <option key={site.id} value={site.id}>{site.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-group">
                                <label>Booking Status</label>
                                <select 
                                    value={filters.status} 
                                    onChange={(e) => setFilters({...filters, status: e.target.value, page: 1})}
                                    className="filter-select"
                                >
                                    <option value="">All Status</option>
                                    <option value="confirmed">Confirmed</option>
                                    <option value="pending">Pending</option>
                                    <option value="cancelled">Cancelled</option>
                                    <option value="completed">Completed</option>
                                </select>
                            </div>

                            <div className="filter-group">
                                <label>Payment Status</label>
                                <select 
                                    value={filters.payment_status} 
                                    onChange={(e) => setFilters({...filters, payment_status: e.target.value, page: 1})}
                                    className="filter-select"
                                >
                                    <option value="">All Payment</option>
                                    <option value="completed">Completed</option>
                                    <option value="pending">Pending</option>
                                    <option value="failed">Failed</option>
                                    <option value="refunded">Refunded</option>
                                </select>
                            </div>

                            <div className="filter-group">
                                <label>From Date</label>
                                <input
                                    type="date"
                                    value={filters.date_from}
                                    onChange={(e) => setFilters({...filters, date_from: e.target.value, page: 1})}
                                    className="filter-input"
                                />
                            </div>

                            <div className="filter-group">
                                <label>To Date</label>
                                <input
                                    type="date"
                                    value={filters.date_to}
                                    onChange={(e) => setFilters({...filters, date_to: e.target.value, page: 1})}
                                    className="filter-input"
                                />
                            </div>

                            <div className="filter-group filter-actions">
                                <button className="btn-clear" onClick={clearFilters}>
                                    <i className="fas fa-times"></i>
                                    Clear Filters
                                </button>
                            </div>
                        </div>

                        {/* Active Filters Display */}
                        {(filters.search || filters.status || filters.payment_status || filters.location_id || filters.site_id || filters.date_from || filters.date_to) && (
                            <div className="active-filters">
                                <span className="active-filters-label">Active Filters:</span>
                                <div className="filter-tags">
                                    {filters.search && (
                                        <span className="filter-tag">
                                            Search: "{filters.search}"
                                            <i className="fas fa-times" onClick={() => removeFilter('search')}></i>
                                        </span>
                                    )}
                                    {filters.status && (
                                        <span className="filter-tag">
                                            Status: {filters.status}
                                            <i className="fas fa-times" onClick={() => removeFilter('status')}></i>
                                        </span>
                                    )}
                                    {filters.payment_status && (
                                        <span className="filter-tag">
                                            Payment: {filters.payment_status}
                                            <i className="fas fa-times" onClick={() => removeFilter('payment_status')}></i>
                                        </span>
                                    )}
                                    {filters.location_id && locations.find(l => String(l.id) === String(filters.location_id)) && (
                                        <span className="filter-tag">
                                            Location: {locations.find(l => String(l.id) === String(filters.location_id))?.name}
                                            <i className="fas fa-times" onClick={() => removeFilter('location_id')}></i>
                                        </span>
                                    )}
                                    {filters.site_id && sites.find(s => String(s.id) === String(filters.site_id)) && (
                                        <span className="filter-tag">
                                            Site: {sites.find(s => String(s.id) === String(filters.site_id))?.name}
                                            <i className="fas fa-times" onClick={() => removeFilter('site_id')}></i>
                                        </span>
                                    )}
                                    {filters.date_from && (
                                        <span className="filter-tag">
                                            From: {formatDate(filters.date_from)}
                                            <i className="fas fa-times" onClick={() => removeFilter('date_from')}></i>
                                        </span>
                                    )}
                                    {filters.date_to && (
                                        <span className="filter-tag">
                                            To: {formatDate(filters.date_to)}
                                            <i className="fas fa-times" onClick={() => removeFilter('date_to')}></i>
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Results Info */}
                    {!loading && (
                        <div className="results-info">
                            <span>Showing {bookings.length} of {pagination.total} bookings</span>
                            {pagination.total > 0 && (
                                <span className="page-info">
                                    Page {pagination.page} of {pagination.pages}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Bookings Table */}
                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading bookings...</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="bookings-table">
                                <thead>
                                    <tr>
                                        <th>Booking ID</th>
                                        <th>Customer</th>
                                        <th>Site</th>
                                        <th>Check In/Out</th>
                                        <th>Nights</th>
                                        <th>Total</th>
                                        <th>Booking Status</th>
                                        <th>Payment</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bookings.length > 0 ? (
                                        bookings.map(booking => (
                                            <tr key={booking.id}>
                                                <td>
                                                    <div className="booking-id">
                                                        <strong>{booking.booking_id}</strong>
                                                        <span className="booking-date">
                                                            {formatDateTime(booking.created_at)}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="customer-info">
                                                        <strong>{booking.customer_name}</strong>
                                                        <span className="customer-contact">
                                                            {booking.customer_email}
                                                        </span>
                                                        <span className="customer-phone">
                                                            {booking.customer_phone}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="site-info">
                                                        <strong>{booking.site_name}</strong>
                                                        <span className="site-location">
                                                            {booking.location_name}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="dates">
                                                        <span className="check-in">
                                                            <i className="fas fa-calendar-plus"></i>
                                                            {formatDate(booking.check_in)}
                                                        </span>
                                                        <span className="check-out">
                                                            <i className="fas fa-calendar-minus"></i>
                                                            {formatDate(booking.check_out)}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <span className="nights-badge">
                                                        {booking.total_nights} {booking.total_nights === 1 ? 'night' : 'nights'}
                                                    </span>
                                                </td>
                                                <td className="text-right">
                                                    <div className="amount-info">
                                                        <strong>{formatCurrency(booking.total_amount)}</strong>
                                                        {booking.advance_amount > 0 && (
                                                            <span className="advance-amount">
                                                                Adv: {formatCurrency(booking.advance_amount)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <select 
                                                        className={`status-select ${booking.booking_status}`}
                                                        value={booking.booking_status}
                                                        onChange={(e) => handleStatusUpdate(booking.id, e.target.value)}
                                                    >
                                                        <option value="confirmed">Confirmed</option>
                                                        <option value="pending">Pending</option>
                                                        <option value="cancelled">Cancelled</option>
                                                        <option value="completed">Completed</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <select 
                                                        className={`payment-select ${booking.payment_status}`}
                                                        value={booking.payment_status}
                                                        onChange={(e) => handlePaymentUpdate(booking.id, e.target.value)}
                                                    >
                                                        <option value="pending">Pending</option>
                                                        <option value="completed">Completed</option>
                                                        <option value="failed">Failed</option>
                                                        <option value="refunded">Refunded</option>
                                                    </select>
                                                </td>
                                                <td className="actions">
                                                    <button 
                                                        className="btn-icon view"
                                                        onClick={() => setSelectedBooking(booking)}
                                                        title="View Details"
                                                    >
                                                        <i className="fas fa-eye"></i>
                                                    </button>
                                                    <button 
                                                        className="btn-icon email"
                                                        onClick={() => window.location.href = `mailto:${booking.customer_email}`}
                                                        title="Send Email"
                                                    >
                                                        <i className="fas fa-envelope"></i>
                                                    </button>
                                                    <button 
                                                        className="btn-icon print"
                                                        onClick={() => window.open(`/admin/camping/bookings/${booking.id}/invoice`)}
                                                        title="Print Invoice"
                                                    >
                                                        <i className="fas fa-print"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="9" className="no-data">
                                                <i className="fas fa-inbox"></i>
                                                <p>No bookings found</p>
                                                <button className="btn-clear" onClick={clearFilters}>
                                                    Clear Filters
                                                </button>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && pagination.pages > 1 && (
                        <div className="pagination">
                            <button 
                                className="page-btn"
                                disabled={pagination.page === 1}
                                onClick={() => handlePageChange(pagination.page - 1)}
                            >
                                <i className="fas fa-chevron-left"></i>
                            </button>
                            
                            {[...Array(Math.min(pagination.pages, 5))].map((_, i) => {
                                let pageNum;
                                if (pagination.pages <= 5) {
                                    pageNum = i + 1;
                                } else if (pagination.page <= 3) {
                                    pageNum = i + 1;
                                } else if (pagination.page >= pagination.pages - 2) {
                                    pageNum = pagination.pages - 4 + i;
                                } else {
                                    pageNum = pagination.page - 2 + i;
                                }
                                
                                return (
                                    <button
                                        key={pageNum}
                                        className={`page-btn ${pagination.page === pageNum ? 'active' : ''}`}
                                        onClick={() => handlePageChange(pageNum)}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                            
                            <button 
                                className="page-btn"
                                disabled={pagination.page === pagination.pages}
                                onClick={() => handlePageChange(pagination.page + 1)}
                            >
                                <i className="fas fa-chevron-right"></i>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Booking Details Modal */}
            {selectedBooking && (
                <BookingDetails
                    booking={selectedBooking}
                    onClose={() => setSelectedBooking(null)}
                    onUpdate={() => {
                        fetchBookings();
                        fetchStats();
                    }}
                />
            )}
        </div>
    );
};

export default AdminCampingBookings;
